import { createElement, memo, useCallback, useRef } from '@max/max';
import TopView from '@max/leez-top-view';
import ModalBaseContainer from '@max/leez-modal-base-container';
import Image from '@hfe/max-image';
import View from '@hfe/max-view';
import Text from '@hfe/max-text';
import Button from '@hfe/max-button';
import './index.scss';
import { BizLineDesc } from '../../types/rewardRecords';
import { formatTimestamp } from '../../utils/time';
import { fenToYuan } from '../../utils/price';
import { parseFreeOrderCardExtra } from '../../utils/rewardRecords';
import { getPriceFontFamily } from '../../utils/style';
import VapView from '../vap-view';
import { FREE_CARD_UNUSED_VAP, FREE_CARD_USED_VAP } from '../../const';
import Animated from '@max/mbc-animated';
import AnimateView from '@max/mbc-animate-view';
import { getFullScreenSize } from '../../utils/layout';
const { fullScreenWidth, fullScreenHeight } = getFullScreenSize();
const priceFontFamily = getPriceFontFamily();
const CONTENT_APPEAR_DELAY = 1640;
function FreeCardModal(props) {
    const { visible, freeOrderCardVO, onClose, onClickUseButton, onOpened } = props;
    const freeOrderInfo = parseFreeOrderCardExtra(freeOrderCardVO) ?? {};
    const cityInfo = freeOrderInfo?.selectedCardGroups?.[0];
    const bindBizName = freeOrderInfo?.bindOrderId && freeOrderInfo.bindBizLine ? BizLineDesc?.[freeOrderInfo?.bindBizLine] : '';
    // 有效期使用 FreeOrderCardExtra.useEndTime（免单卡使用截止时间），而非活动结束时间
    const endTime = formatTimestamp(freeOrderInfo?.useEndTime, 'chinese');
    const yuanPrice = fenToYuan(freeOrderInfo?.amount);
    const isVideoEndRef = useRef(false);
    // 动画状态
    const contentOpacity = useRef(new Animated.Value(0));
    const contentScale = useRef(new Animated.Value(0.5));
    const closeBtnOpacity = useRef(new Animated.Value(0));
    const isUsed = !!freeOrderInfo?.bindOrderId;
    const vapConfig = isUsed ? FREE_CARD_USED_VAP : FREE_CARD_UNUSED_VAP;
    /**
     * 卡片内容出现动画
     * 时间线：1.64s ~ 1.84s 透明度 0%→100%，中心缩放 50%→110%
     * 1.96s 缩放 110%→100%
     */
    const playContentAnimation = useCallback(() => {
        contentOpacity.current.setValue(0);
        contentScale.current.setValue(0.5);
        closeBtnOpacity.current.setValue(0);
        const playContentTimer = setTimeout(() => {
            Animated.parallel([Animated.timing(contentOpacity.current, {
                    toValue: 1,
                    duration: 200,
                    useNativeDriver: true
                }), Animated.sequence([Animated.timing(contentScale.current, {
                        toValue: 1.1,
                        duration: 200,
                        useNativeDriver: true
                    }), Animated.timing(contentScale.current, {
                        toValue: 1,
                        duration: 120,
                        useNativeDriver: true
                    })]), Animated.timing(closeBtnOpacity.current, {
                    toValue: 1,
                    duration: 200,
                    useNativeDriver: true
                })]).start();
            clearTimeout(playContentTimer);
        }, CONTENT_APPEAR_DELAY);
    }, []);
    return <TopView>
      <ModalBaseContainer visible={visible} type="fade" onMaskPress={onClose} onOpened={() => {
        onOpened();
    }} maskStyle={{
        opacity: 0.8
    }} maskAnimationIn={{
        opacity: [0, 0.8],
        duration: 300
    }} 
    // 设置蒙层出场动画结束后的透明度为 0
    maskAnimationOut={{
        opacity: [0.8, 0],
        duration: 300
    }}>
        <View className="free-card-modal__outer-container" style={{
        height: fullScreenHeight
    }}>
          <View className="free-card-modal__vap-wrapper" style={{
        height: fullScreenHeight
    }} onClick={() => {
        if (isVideoEndRef.current) {
            onClose();
        }
    }}>
            <VapView {...vapConfig} autoPlay loop={false} keepLastFrame isFullScreen widthHeightRatio={750 / 1624} height={fullScreenHeight} width={fullScreenWidth} onStart={() => {
        playContentAnimation();
    }} onVideoEnd={() => {
        isVideoEndRef.current = true;
    }}/>
          </View>
          <View className="free-card-modal__inner-container">
            <AnimateView animateStyles={{
        opacity: contentOpacity.current,
        transform: [{
                scale: contentScale.current
            }]
    }}>
              <View x-if={!freeOrderInfo?.bindOrderId} className="unused-free-card-modal__wrapper">
                <View className="unused-free-card-modal__title-wrapper">
                  <Text className="unused-free-card-modal__title-text">酒旅免单卡 最高免单</Text>
                </View>
                <View className="unused-free-card-modal__amount-wrapper">
                  <Text className="unused-free-card-modal__amount-prefix" style={{
        fontFamily: priceFontFamily
    }}>
                    ¥
                  </Text>
                  <Text className="unused-free-card-modal__amount-content" style={{
        fontFamily: priceFontFamily
    }}>
                    {yuanPrice}
                  </Text>
                </View>
                <Button className="unused-free-card-modal__use-button" onPress={() => onClickUseButton({
        cityInfo
    })}>
                  <Text className="unused-free-card-modal__use-button-text">去使用</Text>
                </Button>
                <View x-if={endTime} className="unused-free-card-modal__expire-desc-wrapper">
                  <Text className="unused-free-card-modal__expire-desc-text">有限期：{endTime}</Text>
                </View>
              </View>
              <View x-if={freeOrderInfo?.bindOrderId} className="used-free-card-modal__wrapper">
                <View className="used-free-card-modal__tag-wrapper"></View>
                <Text className="used-free-card-modal__title">{bindBizName}已免单</Text>
                <View className="used-free-card-modal__amount-wrapper">
                  <Text className="used-free-card-modal__amount-prefix" style={{
        fontFamily: priceFontFamily
    }}>
                    ¥
                  </Text>
                  <Text className="used-free-card-modal__amount-content" style={{
        fontFamily: priceFontFamily
    }}>
                    {yuanPrice}
                  </Text>
                </View>
                <Text numberOfLines={2} className="used-free-card-modal__bind-name-text">
                  免单{bindBizName}：{freeOrderInfo?.bindName}
                </Text>
                <Button className="used-free-card-modal__use-button" onPress={props.onClose}>
                  <Text className="used-free-card-modal__use-button-text">知道了</Text>
                </Button>
                <Text className="used-free-card-modal__expire-desc-text">离店后5个工作日返至【我的-余额】</Text>
              </View>
            </AnimateView>
            <AnimateView animateStyles={{
        opacity: closeBtnOpacity.current
    }} style={{
        width: '750rpx'
    }}>
              <Button className="modal-close-button" onPress={() => {
        onClose();
    }}>
                <Image className="modal-close-button-img" source={{
        uri: 'https://p0.meituan.net/ingee/dacb6cc62b5d35e0e749fc7a73bbe7821798.png'
    }}/>
              </Button>
            </AnimateView>
          </View>
        </View>
      </ModalBaseContainer>
    </TopView>;
}
export default memo(FreeCardModal);
